import { Flex, Text, Box } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { ReactComponent as AddCardIcon } from 'assets/svgs/add-card.svg';
import { ReactComponent as MastercardIcon } from 'assets/svgs/mastercard.svg';
import { ReactComponent as VisaIcon } from 'assets/svgs/visa.svg';
import Button from 'components/Button/Button';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { mq } from 'styles/breakpoints';
import capitalize from 'utils/capitalize';
import useBreakpoint from 'hooks/useBreakpoint';
import { RootState } from 'store';
import { isPaymentMethodMissingSelector } from 'modules/payments/paymentsSlice';
import { currentUserSelector } from 'modules/user/userSlice';
import { appContextSelector, isCorporateThemeSelector } from 'modules/app/appSlice';
import CurrencyInfo from './CurrencyInfo';
import { isPaymentMethodInvalid } from './utils';

const cardIcons: { [key: string]: FC } = {
  visa: VisaIcon,
  mastercard: MastercardIcon,
};

const Wrapper = styled(Flex)`
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  ${mq.sm} {
    width: 300px;
  }
`;

interface Props {
  onClick?: () => void;
  isModal?: boolean;
}

const PaymentMethodView: FC<Props> = ({ onClick, isModal }) => {
  const { paymentMethods } = useSelector((state: RootState) => state.payments);
  const isPaymentMethodMissing = useSelector(isPaymentMethodMissingSelector);
  const paymentMethod = paymentMethods?.find(({ type }) => type === 'Stripe');
  const context = useSelector(appContextSelector);
  const isCardPaymentMethodInvalid = isPaymentMethodInvalid({ paymentMethod, country: 'true', context });
  const currentAccount = useSelector(currentUserSelector);
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const CardIcon = cardIcons[paymentMethod?.cardBrand ?? ''];
  const { t } = useTranslation('paymentMethod');
  const { isMobile } = useBreakpoint();

  const currency = currentAccount?.customer?.payment?.currency;

  const fontSize = isCorporateTheme ? 'md' : 'lgMin';

  if (!isModal && (isPaymentMethodMissing || isCardPaymentMethodInvalid)) {
    return (
      <Flex direction="column">
        <Text fontSize={isCorporateTheme ? 'md' : 'mdMax'} fontWeight={500} color="secondary.700">
          {t('noCardText')}
        </Text>
        <Button w={isMobile ? '100%' : '234px'} mt="15px" onClick={onClick} leftIcon={<AddCardIcon />}>
          <span>{t('addPaymentMethod')}</span>
        </Button>
      </Flex>
    );
  }

  return (
    <Flex flexDir="column">
      <Wrapper border="1px solid" borderColor="secondary.300" backgroundColor="white" borderRadius="base">
        <Flex p="20px">
          {CardIcon && <CardIcon />}
          <Flex direction="column" ml="20px">
            <Text fontSize={fontSize}>
              {capitalize(paymentMethod?.cardFunding ?? '')} {capitalize(paymentMethod?.cardBrand ?? '')} ({paymentMethod?.cardLast4 ?? ''})
            </Text>
            <Text fontSize={fontSize}>
              {t('expDate')}: {(paymentMethod?.cardExpMonth ?? 0) < 10 ? `0${paymentMethod?.cardExpMonth}` : paymentMethod?.cardExpMonth}/
              {(paymentMethod?.cardExpYear ?? '').toString().slice(2)}
            </Text>
            <Text fontSize={fontSize}>{paymentMethod?.cardBillingName}</Text>
            {!isCorporateTheme && (
              <Text fontSize={fontSize}>
                {t('currency')}: {currency}
              </Text>
            )}
          </Flex>
        </Flex>
      </Wrapper>
      <Box mt="50px" w="100%">
        <CurrencyInfo />
      </Box>
    </Flex>
  );
};

export default PaymentMethodView;
